import React, { useContext } from 'react'
import { connect } from 'react-redux'

import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import OfferPricing from 'components/Common/OfferPricing'
import Spacing from 'components/Common/Spacing'
import { HowItWorksItem } from 'components/Common/HowItWorks'
import config from 'constants/config'
import OfferTimeLimitedText from '../OfferTimeLimitedText'
import isTimeLimited from 'lib/isTimeLimited'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'components/Luxkit/Button/TextButton'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import { isDepositAvailableOnOffer } from 'lib/depositsUtils'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import Heading from 'components/Luxkit/Typography/Heading'
import CruiseSupportButton from 'components/Cruises/CruiseOfferPage/content/CruiseSupportButton'
import GeoContext from 'contexts/geoContext'

const SummaryPane = styled(Pane).attrs({
  type: 'light',
})`
  line-height: 1;
  border-radius: ${props => props.theme.borderRadius.small};

  ${mediaQueryUp.sm} {
    width: 100%;
  }
`

const ButtonWrapper = styled.div`
  margin-top: ${rem(16)};

  ${mediaQueryDown.xs} {
    @media ( max-height: 500px ) {
      display: none;
    }
  }
`

const SummaryBody = styled(PaneBody).attrs({
  size: 'md',
})`
  > * + * {
    margin-top: ${rem(8)};
  }
`

const StyledOfferPartnershipLabel = styled(OfferPartnershipLabel)`
  margin-top: ${rem(8)};
`

const StyledSecureDepositTagDiv = styled.div`
  margin-left: ${rem(12)};
`

const baseHowItWorksItem = {
  contentSize: 'small',
  iconSize: 30,
}

const bestPriceHowItWorksItem = {
  ...baseHowItWorksItem,
  icon: 'handpicked-partner',
  title: 'Best price guarantee',
  content: "If you find a better price, we'll match it",
}

interface Props {
  availableForPurchase: boolean;
  currentCurrency: string;
  offer: App.TourOffer;
  price: number;
  showPackageOptions: () => void;
}

function FlashTourSummary(props: Props) {
  const {
    availableForPurchase,
    currentCurrency,
    offer,
    price,
    showPackageOptions,
  } = props

  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, null, offer.lowestPricePackage.price)
  const isCruiseOffer = offer.holidayTypes?.includes('Cruises')
  const { partnership } = useContext(GeoContext)
  return (
    <SummaryPane data-testid="SummaryPane">
      <SummaryBody>
        <Pane>
          <PaneBody size="md">
            {isTimeLimited(offer) && <OfferTimeLimitedText />}
            <Spacing mt={16}>
              <WalledContent
                buttonText="Log in to see price"
                enforced={offer.walledGarden}
              >
                { showSecureWithDepositTag &&
                  <StyledSecureDepositTagDiv>
                    <SecureWithDepositTagAndTooltip offerType={offer.type} />
                  </StyledSecureDepositTagDiv>
                }
                {price && <OfferPricing offer={offer} />}
                { partnership && partnership.code !== 'velocity' && <StyledOfferPartnershipLabel
                  offer={offer}
                /> }
              </WalledContent>
            </Spacing>

            {availableForPurchase &&
              <ButtonWrapper>
                <TextButton
                    kind="primary"
                    data-testid="OfferAvailable"
                    onClick={showPackageOptions}
                    fit="flex">
                  View All Tour Options
                </TextButton>
              </ButtonWrapper>
              }
            {!availableForPurchase &&
              <Spacing mt={16}>
                <Heading variant="heading3" data-testid="OfferSoldOut">
                  Sold Out
                </Heading>
              </Spacing>
            }

            {isCruiseOffer && <Spacing mt={16}>
              <CruiseSupportButton />
            </Spacing>}
          </PaneBody>
        </Pane>
        {config.bestPriceGuarantee &&
          <ClickableLink to="/best-price-guarantee">
            <HowItWorksItem
              inline
              item={bestPriceHowItWorksItem}
            />
          </ClickableLink>
        }
      </SummaryBody>
    </SummaryPane>
  )
}

const mapStateToProps = (state: App.State) => ({
  currentCurrency: state.geo.currentCurrency,
})

export default connect(mapStateToProps)(FlashTourSummary)
